import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore'
import { newFirestore } from '../../../../../utils/firebase/firebase'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'

const UserPayments = ({ user }) => {
  const [terminalPayments, setTerminalPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchUserPayments = useCallback(async () => {
    try {
      setLoading(true)
      
      // Fetch only terminal payments where user is associated
      const terminalPaymentsRef = collection(newFirestore, 'terminal_payments')
      const terminalPaymentsQuery = query(
        terminalPaymentsRef, 
        where('user.id', '==', user.id),
        orderBy('created', 'desc')
      )
      const terminalPaymentsSnapshot = await getDocs(terminalPaymentsQuery)
      
      const terminalPaymentsList = []
      terminalPaymentsSnapshot.forEach((doc) => {
        const data = doc.data()
        let createdDate
        try {
          // Handle Firestore timestamp or fallback to regular Date
          if (data.created && typeof data.created.toDate === 'function') {
            createdDate = data.created.toDate()
          } else if (data.created instanceof Date) {
            createdDate = data.created
          } else if (data.created) {
            createdDate = new Date(data.created)
          } else {
            createdDate = new Date()
          }
        } catch (error) {
          console.warn('Error parsing created date for payment:', doc.id, error)
          createdDate = new Date()
        }
        
        terminalPaymentsList.push({
          id: doc.id,
          ...data,
          created: createdDate,
          source: 'terminal_payments'
        })
      })
      
      setTerminalPayments(terminalPaymentsList)
      setError(null)
    } catch (err) {
      console.error('Error fetching user payments:', err)
      setError('Failed to load payments')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    if (user?.id) {
      fetchUserPayments()
    }
  }, [user?.id, fetchUserPayments])

  const formatAmount = (amount, currency = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100)
  }

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const getPaymentTypeIcon = () => {
    return <PointOfSaleIcon />
  }

  const getPaymentTypeLabel = () => {
    return 'Terminal'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return 'success'
      case 'processing': return 'warning'  
      case 'requires_payment_method': return 'error'
      default: return 'default'
    }
  }

  const isTestPayment = (payment) => {
    // Check if payment is marked as test or if payment intent ID starts with test prefix
    return payment.isTest || 
           payment.paymentIntentId?.startsWith('pi_test') || 
           process.env.NODE_ENV === 'development'
  }

  const allPayments = [...terminalPayments]
    .sort((a, b) => new Date(b.created) - new Date(a.created))

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment History
          </Typography>
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment History
          </Typography>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: 'white!important' }}>
        Payment History
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Point of Sale (POS) payments made in-person at the terminal
      </Typography>
      
      {allPayments.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No POS payment history found for this user.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <List>
              {allPayments.map((payment, index) => (
                <Box key={`${payment.source}-${payment.id}`}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getPaymentTypeIcon()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body1" component="span">
                            {formatAmount(payment.amount, payment.currency)}
                          </Typography>
                          <Chip
                            label={payment.status}
                            color={getStatusColor(payment.status)}
                            size="small"
                          />
                          <Chip
                            label={getPaymentTypeLabel()}
                            variant="outlined"
                            size="small"
                          />
                          {isTestPayment(payment) && (
                            <Chip
                              label="TEST"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatTime(payment.created)}
                          </Typography>
                          {payment.description && (
                            <Typography variant="body2" color="text.secondary">
                              {payment.description}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            ID: {payment.paymentIntentId?.slice(-8) || 'N/A'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < allPayments.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default UserPayments